import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BusinessCalendar } from "@helpdesk/sla";

import { ActivityLink } from "../ActivityDrawer";
import { Avatar } from "../ui/Avatar";
import { LaneTaskDialog } from "../LaneTaskDialog";
import { Button } from "../ui/Button";
import { cn } from "../ui/cn";
import { AddCompany } from "./AddCompany";
import { CopyButton } from "../CopyButton";
import { formatPhone } from "../crm/PhoneField";
import { SlaClock } from "../SlaClock";
import { assigneeOf } from "../../lib/agents";
import { api } from "../../lib/api";
import { TicketSalesCall } from "./TicketSalesCall";
import { queryKeys } from "../../lib/queryKeys";
import { useLiveSla, formatRemaining, slaTextClass } from "../../lib/sla";
import { absoluteTime, shortDateTime } from "../../lib/time";
import { describeParticipant, type Participant } from "../../lib/participants";
import type { Pipeline, TicketSummary, User } from "../../types";

interface TicketDetailsPaneProps {
  ticket: TicketSummary;
  /**
   * Everybody on the thread, gathered from the mail's To and Cc lines.
   *
   * Passed in rather than derived here because the panel is handed a ticket
   * summary, which knows about one contact by design -- the messages that say
   * who else is on it belong to the conversation query beside it, and asking
   * for them again from here would be a second request for the same thread.
   */
  people: Participant[];
  pipelines: Pipeline[];
  users: User[];
  calendar: BusinessCalendar;
  onPipelineChange: (pipelineId: number | null) => void;
  onAssigneeChange: (assigneeId: number | null) => void;
  onStatusChange: (status: "open" | "closed") => void;
  currentUserId: number;
  onOpenTicket: (id: number) => void;
}

/**
 * Ticket details: everything about the ticket that is not the conversation.
 *
 * All of the controls live here rather than above the thread, so the middle
 * column is only the ticket itself -- the mail, and the reply. A row of
 * dropdowns sitting between the subject and the first message reads as part of
 * the conversation and is the wrong place to be changing state from.
 */
export function TicketDetailsPane({
  ticket,
  people,
  pipelines,
  users,
  calendar,
  onPipelineChange,
  onAssigneeChange,
  onStatusChange,
  onOpenTicket,
  currentUserId,
}: TicketDetailsPaneProps) {
  const sla = useLiveSla(ticket, pipelines, calendar);
  const isClosed = ticket.status === "closed";
  const isSpam = ticket.isSpam;
  const queryClient = useQueryClient();

  /*
   * The person's line first, then the switchboard, and the switchboard dropped
   * when it is the same string -- a sole trader's contact record routinely
   * carries the one number twice, and listing it under two headings implies a
   * second way through that does not exist.
   */
  const phones = [
    { label: "Phone", number: ticket.contactPhone },
    { label: "Switchboard", number: ticket.companyPhone },
  ].filter(
    (phone, index, all): phone is { label: string; number: string } =>
      phone.number !== null &&
      all.findIndex((other) => other.number === phone.number) === index,
  );

  /*
   * References are edited here rather than only at creation, which is where
   * they were stranded before: an order number is nearly always quoted in the
   * second email, not the first, so a field that could only be filled in on a
   * ticket that did not exist yet was one nobody could use.
   */
  const setRefs = useMutation({
    mutationFn: (refs: Parameters<typeof api.setReferences>[1]) =>
      api.setReferences(ticket.id, refs),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.ticketsRoot });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.ticket(ticket.id),
      });
    },
  });

  /*
   * Spam is toggled here rather than passed down as a callback the way status,
   * assignee and pipeline are. Those three are also driven from the queue's
   * bulk actions, so the mutation lives up with the list; this one has exactly
   * one caller and threading a prop through for it would only spread the
   * ticket's controls across two files again.
   */
  const setSpam = useMutation({
    mutationFn: (isSpam: boolean) => api.setSpam(ticket.id, isSpam),
    onSuccess: () => {
      /* The queue lists too, not just this ticket: marking spam removes the row
         from every view except Spam, and the counts beside them all move. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.ticketsRoot });
      void queryClient.invalidateQueries({ queryKey: queryKeys.viewCounts });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.ticket(ticket.id),
      });
    },
  });

  /*
   * Other conversations with the same contact. Loaded here rather than with the
   * ticket so a slow lookup never delays the mail itself appearing.
   */
  const related = useQuery({
    queryKey: queryKeys.related(ticket.id),
    queryFn: () => api.relatedTickets(ticket.id),
    staleTime: 60_000,
  });

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-surface">
      <Section title="Status">
        <Button
          variant={isClosed ? "secondary" : "primary"}
          className="w-full"
          onClick={() => onStatusChange(isClosed ? "open" : "closed")}
        >
          {isClosed ? "Reopen ticket" : "Close ticket"}
        </Button>
        <p className="mt-1.5 text-xs text-faint">
          {isClosed
            ? "Closed. A customer reply reopens it automatically."
            : "Open and in the inbox."}
        </p>

        {/*
          Spam sits under status because it is the other answer to "what is this
          ticket", and because the two are read together: a thing you close is
          work that is finished, and a thing you mark as spam was never work.

          Secondary in both directions, deliberately. Neither is the button you
          came to this panel to press, and a primary "Not spam" would be the
          loudest thing on a panel whose actual job is the ticket above it.
        */}
        <Button
          variant="secondary"
          className="mt-2 w-full"
          disabled={setSpam.isPending}
          onClick={() => setSpam.mutate(!isSpam)}
        >
          {isSpam ? "Not spam" : "Mark as spam"}
        </Button>
        {isSpam ? (
          <p className="mt-1.5 text-xs text-faint">
            In Spam, and out of every other view. If this sender keeps being
            caught, an admin can allow them in Settings → Helpdesk.
          </p>
        ) : null}
      </Section>

      <Section title="Assignee">
        {/*
          A one-click claim, because unassigned is the default state of all new
          work and picking it up is the commonest thing done on this panel. The
          dropdown stays for handing a ticket to somebody else.
        */}
        {ticket.assigneeId !== currentUserId ? (
          <Button
            variant={ticket.assigneeId === null ? "primary" : "secondary"}
            className="mb-2 w-full"
            onClick={() => onAssigneeChange(currentUserId)}
          >
            {ticket.assigneeId === null ? "Claim this ticket" : "Take over"}
          </Button>
        ) : null}

        <div className="flex items-center gap-2">
          <Avatar
            size="md"
            agent={assigneeOf(users, ticket.assigneeId, ticket.assigneeName)}
          />
          <select
            value={ticket.assigneeId ?? ""}
            onChange={(event) =>
              onAssigneeChange(
                event.target.value === "" ? null : Number(event.target.value),
              )
            }
            className={cn(
              "min-w-0 flex-1 rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-content",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            )}
          >
            <option value="">Unassigned</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>
      </Section>

      <Section title="Pipeline">
        <select
          value={ticket.pipelineId ?? ""}
          onChange={(event) =>
            onPipelineChange(
              event.target.value === "" ? null : Number(event.target.value),
            )
          }
          className={cn(
            "w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-content",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          )}
        >
          <option value="">No pipeline</option>
          {/*
            Names only. The SLA each pipeline carries is a manager's setting,
            not something an agent picks a pipeline by -- and printing "(2h)"
            here invites treating the target as the reason for the choice.
          */}
          {pipelines.map((pipeline) => (
            <option key={pipeline.id} value={pipeline.id}>
              {pipeline.name}
            </option>
          ))}
        </select>
      </Section>

      <Section title="SLA">
        <div className="flex items-center gap-3">
          <SlaClock sla={sla} size="md" />
          <div className="min-w-0">
            <div className={cn("text-sm font-medium", slaTextClass(sla.level))}>
              {sla.level === null
                ? "Not measured"
                : sla.level === "breached"
                  ? "Breached"
                  : formatRemaining(sla.remainingMinutes) + " remaining"}
            </div>
            <div className="text-xs text-muted">
              {sla.targetMinutes === null
                ? ticket.pipelineId === null
                  ? "No pipeline set"
                  : "This pipeline has no target"
                : `${formatRemaining(sla.elapsedMinutes)} of ${formatRemaining(sla.targetMinutes)} used`}
            </div>
            {sla.paused ? (
              <div className="text-xs text-faint">
                Paused — outside business hours
              </div>
            ) : null}
          </div>
        </div>
      </Section>

      <Section title="Contact">
        {/*
          Both names open their history rather than sitting here as text. This
          panel is where somebody looks when they want to know who they are
          dealing with, and "who are they" and "what have we said to them" are
          the same question a moment apart.
        */}
        <FieldRow label="Company">
          {ticket.companyName ? (
            <ActivityLink
              kind="company"
              id={ticket.companyId}
              name={ticket.companyName}
              className="text-content"
            />
          ) : ticket.contactId ? (
            /* Nothing files this by itself any more -- see `AddCompany`, and
               `resolveForEmail` for why. The blank is an invitation rather
               than a dash somebody has to know what to do about. */
            <AddCompany
              contactId={ticket.contactId}
              contactEmail={ticket.contactEmail}
              ticketId={ticket.id}
            />
          ) : (
            /* No contact to file. A ticket raised against nobody -- there is
               nothing here to hang a company on. */
            <span className="text-faint">—</span>
          )}
        </FieldRow>
        <FieldRow label="Name">
          {ticket.contactName ? (
            <ActivityLink
              kind="contact"
              id={ticket.contactId}
              name={ticket.contactName}
              className="text-content"
            />
          ) : (
            <span className="text-faint">—</span>
          )}
        </FieldRow>
        <div className="flex items-baseline gap-2 py-0.5 text-sm">
          <span className="w-20 shrink-0 text-xs text-muted">Email</span>
          {ticket.contactEmail ? (
            <CopyButton
              value={ticket.contactEmail}
              what="email address"
              className="min-w-0 text-content"
            />
          ) : (
            <span className="text-faint">—</span>
          )}
        </div>

        {/*
          Numbers to ring, and the reason this panel carries them at all: the
          answer to a ticket is often a two-minute phone call, and until now
          making it meant leaving the ticket for the customer's CRM record to
          read a number off it.

          Only rendered when there is one, unlike the rows above. Those three
          always say something -- a ticket with no company on it is a fact
          worth seeing -- whereas most tickets have no number, and two dashes
          on every one of them would push the SLA and the references further
          down the panel to report an absence nobody was asking about.
        */}
        {phones.map((phone) => (
          <FieldRow key={phone.label} label={phone.label}>
            {/* A real tel: anchor, dialled as stored and grouped to read --
                the same pairing the CRM and the sales desk make. */}
            <a
              href={`tel:${phone.number}`}
              className="text-content hover:text-brand-600 dark:hover:text-brand-400"
            >
              {formatPhone(phone.number)}
            </a>
          </FieldRow>
        ))}
      </Section>

      {/*
        Sales calls on this customer, next to the person they are about.

        Under Contact rather than at the bottom: the question it answers --
        "is somebody already ringing them, and should I get a call raised" --
        comes up while reading who the mail is from, not after scrolling past
        the references.

        Only on a ticket with a company. A sales call hangs off an account, the
        same rule the CRM and the meetings diary follow, and offering it on an
        unfiled ticket would be offering something the dialog cannot complete.
        Those tickets show *Add company* in the section above, which is the
        step that has to come first anyway.
      */}
      {ticket.companyId !== null && ticket.companyName ? (
        <Section title="Sales call">
          <TicketSalesCall
            companyId={ticket.companyId}
            companyName={ticket.companyName}
            currentUserId={currentUserId}
            isAdmin={
              users.find((user) => user.id === currentUserId)?.role === "admin"
            }
          />
        </Section>
      ) : null}

      {/*
        Everyone the mail actually went to, which is a different list from the
        contact above and routinely a longer one. Rendered only when it says
        something the Contact section did not: on a two-party ticket it would
        be the same address twice under two headings.

        This is the panel that can afford the full list -- it scrolls, and
        nothing above it is competing for the space. The header over the thread
        shows the same people on one truncated line and links here in spirit:
        the question "who else is reading this" is answered at a glance there
        and in full here.
      */}
      {people.length > 1 ? (
        <Section title={`On this ticket (${people.length})`}>
          <ul>
            {people.map((person) => (
              <li
                key={person.address.email}
                title={describeParticipant(person)}
                className="flex items-baseline gap-2 py-0.5 text-sm"
              >
                {/*
                  Wrote / To / Cc. Narrower than the label column the rows above
                  use, and quieter: the address is the thing being read here,
                  and at this width every character the tag takes is one the
                  address loses to an ellipsis. "Wrote" outranks the other two --
                  somebody who has replied is part of the conversation however
                  they were addressed.
                */}
                <span className="w-9 shrink-0 text-[10px] uppercase tracking-wide text-faint">
                  {person.wrote ? "Wrote" : person.copied ? "Cc" : "To"}
                </span>
                <span className="min-w-0 flex-1">
                  {person.address.name ? (
                    <span className="block truncate text-content">
                      {person.address.name}
                    </span>
                  ) : null}
                  <CopyButton
                    value={person.address.email}
                    what="email address"
                    className="min-w-0 text-xs text-muted"
                  />
                </span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section title="Ticket">
        <div className="flex items-baseline gap-2 py-0.5 text-sm">
          <span className="w-20 shrink-0 text-xs text-muted">ID</span>
          <CopyButton
            value={String(ticket.id)}
            label={`#${ticket.id}`}
            what="ticket ID"
            className="text-content tabular-nums"
          />
          {/*
            Beside the ID because it answers the same request -- "send me that
            ticket" -- for somebody who wants to click rather than search.

            Through All open rather than the view it is being read in, unlike a
            message's link in the thread: this is for handing the ticket to a
            colleague, and landing them in the sender's saved view or personal
            queue would put a list round it that is not theirs. `/v/open/:id`
            opens a ticket in any status, closed included, which is what every
            other link to a ticket in the SPA already relies on.
          */}
          <CopyButton
            value={`${window.location.origin}/v/open/${ticket.id}`}
            label="Copy link"
            what="ticket link"
            className="text-xs text-muted"
          />
        </div>
        <Field label="Last email" value={absoluteTime(ticket.lastMessageAt)} />
        <Field
          label="Waiting since"
          value={
            ticket.awaitingResponseSince
              ? absoluteTime(ticket.awaitingResponseSince)
              : "Not waiting on us"
          }
        />
      </Section>

      <Section title="References">
        <ReferenceField
          ticketId={ticket.id}
          label="Order"
          value={ticket.orderNumber}
          placeholder="NXL-12345678"
          onCommit={(orderNumber) => setRefs.mutate({ orderNumber })}
        />
        <ReferenceField
          ticketId={ticket.id}
          label="Quote"
          value={ticket.quoteNumber}
          placeholder="Quote number"
          onCommit={(quoteNumber) => setRefs.mutate({ quoteNumber })}
        />
        <ReferenceField
          ticketId={ticket.id}
          label="Returns"
          value={ticket.returnsReference}
          placeholder="RMA reference"
          onCommit={(returnsReference) => setRefs.mutate({ returnsReference })}
        />
      </Section>

      <LaneSection ticket={ticket} onCommit={(laneTask) => setRefs.mutate({ laneTask })} />

      <ThreadSummary ticketId={ticket.id} />

      {/*
        Other conversations with this person. Only rendered when there are any:
        an empty "no other conversations" panel is a permanent fixture that says
        nothing, on the majority of tickets.
      */}
      {related.data && related.data.length > 0 ? (
        <Section title={`Other conversations (${related.data.length})`}>
          <ul className="-mx-1">
            {related.data.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => onOpenTicket(entry.id)}
                  className={cn(
                    "flex w-full items-baseline gap-2 rounded px-1 py-1 text-left",
                    "hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
                  )}
                >
                  <span
                    className={cn(
                      "min-w-0 flex-1 truncate text-sm",
                      entry.status === "closed" ? "text-muted" : "text-content",
                    )}
                  >
                    {entry.subject}
                  </span>
                  <span
                    className="shrink-0 text-[11px] tabular-nums text-faint"
                    title={absoluteTime(entry.lastMessageAt)}
                  >
                    {shortDateTime(entry.lastMessageAt)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-b border-line px-4 py-3">
      <h3 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted">
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * The linked Lane task: its state, and the way to create one.
 *
 * The reference field stays editable and stays free text -- it predates this
 * integration and agents write notes in it -- but a value the server recognises
 * as a task id resolves into a real link with the task's title and state. That
 * is what makes pasting an existing task into the box work as well as pressing
 * the button.
 */
function LaneSection({
  ticket,
  onCommit,
}: {
  ticket: TicketSummary;
  onCommit: (value: string | null) => void;
}) {
  const [composing, setComposing] = useState(false);

  const link = useQuery({
    queryKey: queryKeys.laneLink(ticket.id),
    queryFn: () => api.laneLink(ticket.id),
    staleTime: 30_000,
  });

  const connection = useQuery({
    queryKey: queryKeys.laneConnection,
    queryFn: api.laneConnection,
    staleTime: 5 * 60_000,
  });

  const enabled = link.data?.enabled ?? false;
  const task = link.data?.link ?? null;
  const connected = connection.data?.connected ?? false;

  return (
    <Section title="Lane task">
      {task ? (
        <div className="mb-2">
          <a
            href={task.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            {task.title ?? task.taskId}
          </a>

          <div className="mt-0.5 text-xs text-muted">
            {task.assigneeNames.length > 0
              ? task.assigneeNames.join(", ")
              : "Unassigned in Lane"}
          </div>

          {/*
            Completion is the state worth calling out: it is what reopens a
            closed ticket, so somebody arriving at the ticket needs to see why.
          */}
          {task.completedAt ? (
            <div className="mt-1 text-xs font-medium text-sla-ok">
              Completed {shortDateTime(task.completedAt)}
            </div>
          ) : (
            <div className="mt-1 text-xs text-faint">Open in Lane</div>
          )}
        </div>
      ) : null}

      <ReferenceField
        ticketId={ticket.id}
        label="Reference"
        value={ticket.laneTask}
        placeholder="Paste a task id or link"
        onCommit={onCommit}
      />

      {enabled && !task ? (
        connected ? (
          <button
            type="button"
            onClick={() => setComposing(true)}
            className={cn(
              "mt-2 rounded-md border border-line px-2 py-1 text-xs text-content",
              "hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            )}
          >
            Create task
          </button>
        ) : (
          /*
            Not connected. A link rather than a button, because the
            authorisation server needs a top-level navigation -- and it is
            offered here rather than hidden behind Settings, since this is the
            moment somebody actually wants it.
          */
          <div className="mt-2">
            <a
              href="/api/lane/oauth/start"
              className="inline-flex rounded-md bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-700"
            >
              Connect Lane to raise tasks
            </a>
            <p className="mt-1 text-xs text-faint">
              Tasks you raise will show as yours in Lane.
            </p>
          </div>
        )
      ) : null}

      {!enabled ? (
        <p className="mt-1.5 text-xs text-faint">
          Lane is not configured on this server.
        </p>
      ) : null}

      {composing ? (
        <LaneTaskDialog ticket={ticket} onClose={() => setComposing(false)} />
      ) : null}
    </Section>
  );
}

/**
 * Bullet-point summary of the conversation.
 *
 * At the bottom, and behind a button. A long thread that has been round three
 * people is exactly what an agent picking it up has to read in full before they
 * can act, and this is the cheap version of that -- but summarising costs a
 * paid API call, and most tickets are two messages long and want nothing. So
 * the panel loads whatever is cached for free and only spends money when asked.
 */
function ThreadSummary({ ticketId }: { ticketId: number }) {
  const queryClient = useQueryClient();

  const summary = useQuery({
    queryKey: queryKeys.summary(ticketId),
    queryFn: () => api.ticketSummary(ticketId),
    staleTime: 60_000,
  });

  const generate = useMutation({
    mutationFn: () => api.generateTicketSummary(ticketId),
    onSuccess: (result) =>
      queryClient.setQueryData(queryKeys.summary(ticketId), result),
  });

  // Nothing configured: say so once rather than offering a button that can
  // only fail.
  if (summary.data && !summary.data.enabled) {
    return (
      <Section title="Summary">
        <p className="text-xs text-faint">
          Summaries are not configured on this server.
        </p>
      </Section>
    );
  }

  const current = summary.data?.summary ?? null;
  const error =
    generate.error instanceof Error ? generate.error.message : null;

  return (
    <Section title="Summary">
      {current ? (
        <>
          <ul className="space-y-1">
            {current.bullets.map((bullet, index) => (
              <li
                key={index}
                className="flex gap-1.5 text-xs leading-relaxed text-content"
              >
                <span aria-hidden className="text-faint">
                  •
                </span>
                <span className="min-w-0">{bullet}</span>
              </li>
            ))}
          </ul>

          {/*
            A summary that predates the customer's latest email is worse than
            none if nobody says so -- it reads as current.
          */}
          {!current.fresh ? (
            <p className="mt-1.5 text-[11px] text-amber-600">
              New messages have arrived since this was written.
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-xs text-faint">
          {summary.isLoading ? "Loading\u2026" : "Not summarised yet."}
        </p>
      )}

      {error ? <p className="mt-1.5 text-xs text-red-600">{error}</p> : null}

      <button
        type="button"
        onClick={() => generate.mutate()}
        disabled={generate.isPending}
        className={cn(
          "mt-2 rounded-md border border-line px-2 py-1 text-xs text-content",
          "hover:bg-surface-2 disabled:opacity-50",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        )}
      >
        {generate.isPending
          ? "Summarising\u2026"
          : current
            ? "Regenerate"
            : "Summarise thread"}
      </button>
    </Section>
  );
}

/**
 * One reference, saved when the field loses focus.
 *
 * Uncontrolled, so typing never waits on a round trip, and keyed on the ticket
 * and the stored value so switching tickets or a change arriving over the
 * socket replaces what is in the box instead of leaving the previous ticket's
 * order number sitting there.
 */
function ReferenceField({
  ticketId,
  label,
  value,
  placeholder,
  onCommit,
}: {
  ticketId: number;
  label: string;
  value: string | null;
  placeholder: string;
  onCommit: (value: string | null) => void;
}) {
  return (
    <div className="flex items-baseline gap-2 py-0.5 text-sm">
      <label
        htmlFor={`ref-${label}-${ticketId}`}
        className="w-20 shrink-0 text-xs text-muted"
      >
        {label}
      </label>
      <input
        id={`ref-${label}-${ticketId}`}
        key={`${ticketId}:${value ?? ""}`}
        defaultValue={value ?? ""}
        placeholder={placeholder}
        onBlur={(event) => {
          const raw = event.target.value.trim();
          const next = raw === "" ? null : raw;
          // Nothing typed, or typed and reverted -- either way no write.
          if (next === value) return;
          onCommit(next);
        }}
        className={cn(
          "min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-sm text-content",
          "hover:border-line focus:border-line focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          "placeholder:text-faint",
        )}
      />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <FieldRow label={label}>
      <span className="truncate text-content">
        {value ?? <span className="text-faint">—</span>}
      </span>
    </FieldRow>
  );
}

/** The label-and-value shape, for rows whose value is more than text. */
function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-2 py-0.5 text-sm">
      <span className="w-20 shrink-0 text-xs text-muted">{label}</span>
      <span className="min-w-0">{children}</span>
    </div>
  );
}
